import moment from 'moment';
import { UIEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import './tabel-rows.scss';

import { TableFilter } from '~modules/tabel/api';
import Status from '~modules/tabel/interfaces/status';
import { TabelRowDto } from '~modules/tabel/model/tabel-row.dto';
import { routerKeys } from '~shared/router';
import { CheckBox } from '~shared/ui';
import { Button } from '~shared/ui/button/button';
import Table from '~shared/ui/table/table';

import { RegenerateByUserModal } from './regenerate-by-user-modal/regenerate-by-user-modal';
import StatusIndicator, { StatusType } from './status-indicator/status-indicator';
import { useHeaders } from './use-headers';
import { useTabelRows } from './use-table-rows';

// За сколько до конца списка начинать догрузку: не меньше экрана,
// чтобы следующая порция успевала прийти до того, как пользователь до неё доскроллит
const LOAD_MORE_OFFSET_PX = 500;

// Сколько ждать ответа, прежде чем показать курсор ожидания
const BUSY_CURSOR_DELAY_MS = 250;

function isRowInDateRange(row: TabelRowDto, startDate: Date, endDate: Date): boolean {
	const rowDate = moment(row.date).startOf('day');
	return (
		rowDate.isSameOrAfter(moment(startDate).startOf('day')) &&
		rowDate.isSameOrBefore(moment(endDate).startOf('day'))
	);
}

export function TabelRowsPage() {
	const [filter, setFilter] = useState<TableFilter>(new TableFilter());
	const [selectByDates, setSelectByDates] = useState<boolean>(false);
	const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set());
	const [status, setStatus] = useState<StatusType>('updated');
	const [isRegenerateByUserOpen, setIsRegenerateByUserOpen] = useState<boolean>(false);
	const tableScrollRef = useRef<HTMLDivElement>(null);

	const canSelectByDates = Boolean(filter.startDate && filter.endDate);

	const handleSaved = useCallback(() => {
		setStatus('updated');
	}, []);

	const clearSelection = () => {
		setSelectByDates(false);
		setSelectedRows(new Set());
	};

	const handlePosted = () => {
		clearSelection();
	};

	const handleReverted = () => {
		clearSelection();
	};

	const handleDeleted = () => {
		clearSelection();
	};

	const handleDownloaded = () => {
		setStatus('updated');
	};

	const { list, pending, loadingMore, hasMore, adjust, del, post, revert, regenerate, refresh, loadMore } =
		useTabelRows(filter, handleSaved, handleDownloaded, handlePosted, handleReverted, handleDeleted);

	// Без обеих дат фильтра массовое выделение недоступно
	useEffect(() => {
		if (!canSelectByDates) {
			setSelectByDates(false);
			setSelectedRows(new Set());
		}
	}, [canSelectByDates]);

	// Выделяем строки, попадающие в выбранный период (по умолчанию — вчера–сегодня)
	useEffect(() => {
		if (!selectByDates || !filter.startDate || !filter.endDate) return;

		setSelectedRows(
			new Set(
				list
					.filter(row => !row.expired && isRowInDateRange(row, filter.startDate!, filter.endDate!))
					.map(row => row.id),
			),
		);
	}, [selectByDates, list, filter.startDate, filter.endDate]);

	const handleTableScroll = (event: UIEvent<HTMLDivElement>) => {
		if (!hasMore) return;
		const target = event.currentTarget;
		const distanceToBottom = target.scrollHeight - target.scrollTop - target.clientHeight;
		if (distanceToBottom < Math.max(LOAD_MORE_OFFSET_PX, target.clientHeight)) {
			loadMore();
		}
	};

	const autoLoadAttemptsRef = useRef(0);
	const prevListLengthRef = useRef(0);

	useEffect(() => {
		if (list.length === 0) {
			autoLoadAttemptsRef.current = 0;
			prevListLengthRef.current = 0;
		}
	}, [list.length]);

	useEffect(() => {
		if (pending || loadingMore || !hasMore) return;
		// Первая страница ещё не пришла — иначе догрузка стартует параллельно с ней
		if (list.length === 0) return;
		const scrollContainer = tableScrollRef.current;
		if (!scrollContainer) return;
		// Пока запаса прокрутки меньше экрана — догружаем, чтобы пользователю было куда скроллить
		const runway = scrollContainer.scrollHeight - scrollContainer.clientHeight;
		if (runway > Math.max(LOAD_MORE_OFFSET_PX, scrollContainer.clientHeight)) return;
		if (list.length === prevListLengthRef.current) return;
		if (autoLoadAttemptsRef.current >= 10) return;

		prevListLengthRef.current = list.length;
		autoLoadAttemptsRef.current += 1;
		loadMore();
	}, [list.length, hasMore, pending, loadingMore, loadMore]);

	// Курсор ожидания только если порция реально не успела прийти:
	// при быстром ответе подгрузка должна остаться незаметной
	const [showBusyCursor, setShowBusyCursor] = useState(false);

	useEffect(() => {
		if (!loadingMore) {
			setShowBusyCursor(false);
			return;
		}
		const timeout = setTimeout(() => setShowBusyCursor(true), BUSY_CURSOR_DELAY_MS);
		return () => clearTimeout(timeout);
	}, [loadingMore]);

	const handleTurnRowSelection = (id: number) =>
		setSelectedRows(prevState => {
			const nextState = new Set(prevState);
			if (nextState.has(id)) nextState.delete(id);
			else nextState.add(id);
			return nextState;
		});

	const handleTurnSelectionByDates = () => {
		setSelectByDates(prev => {
			if (prev) {
				setSelectedRows(new Set());
			}
			return !prev;
		});
	};

	const handleChangeFilter = (nextFilter: Partial<TableFilter>) => {
		const datesChanged = 'startDate' in nextFilter || 'endDate' in nextFilter;
		const startDate = 'startDate' in nextFilter ? nextFilter.startDate : filter.startDate;
		const endDate = 'endDate' in nextFilter ? nextFilter.endDate : filter.endDate;

		setFilter(prevState => ({ ...prevState, ...nextFilter }));

		// При смене периода выделяем строки по новым датам (если обе заданы)
		if (datesChanged) {
			if (startDate && endDate) {
				setSelectByDates(true);
			} else {
				setSelectByDates(false);
				setSelectedRows(new Set());
			}
		}
	};

	const headers = useHeaders(
		selectedRows,
		selectByDates,
		filter,
		handleTurnRowSelection,
		handleTurnSelectionByDates,
		handleChangeFilter,
		adjust,
		() => {
			setStatus('editing');
		},
	);

	const acceptRowClassName = (row: TabelRowDto): string => {
		if (row.status === Status.COMPLETED) return 'row-disabled';
		else if (row.expired) return 'row-disabled';
		else if (row.deviation && row.deviation.criticality === 'warning') return 'row-warning';
		else if (row.deviation && row.deviation.criticality === 'acceptable_error') return 'row-error';
		else if (row.deviation && row.deviation.criticality === 'error') return 'row-error';
		else if (row.checkin_start && row.checkin_end) return 'row-good';
		return '';
	};

	return (
		<div className="tabel page">
			<div className="tabel__content">
				<div className="actions">
					<div>
						<div className="checkbox-container">
							<CheckBox
								checked={filter.withoutActualStay}
								onChange={() =>
									setFilter(prev => ({ ...prev, withoutActualStay: !prev.withoutActualStay }))
								}
							/>
							<span>Показать сотрудников без фактического пребывания</span>
						</div>
						<div className="checkbox-container">
							<CheckBox
								checked={filter.withoutCompletedRows}
								onChange={() =>
									setFilter(prev => ({ ...prev, withoutCompletedRows: !prev.withoutCompletedRows }))
								}
							/>
							<span>Скрыть проведенные табель строки</span>
						</div>
						{canSelectByDates && (
							<div className="checkbox-container">
								<CheckBox checked={selectByDates} onChange={handleTurnSelectionByDates} />
								<span>Выделить табель-строки за период</span>
							</div>
						)}
					</div>
					<StatusIndicator status={pending && !loadingMore ? 'download' : status} />
					<Link to={routerKeys.tabel.addUser()} className="button button--solid">
						Добавить сотрудника
					</Link>
					<Button
						variant="outline"
						disabled={pending}
						onClick={() => {
							setStatus('download');
							regenerate();
						}}
					>
						Загрузить данные
					</Button>
					<Button variant="outline" onClick={() => setIsRegenerateByUserOpen(true)}>
						Пересобрать для сотрудника
					</Button>
					<Button onClick={() => del(Array.from(selectedRows))}>Удалить</Button>
					<Button onClick={() => post(Array.from(selectedRows))}>Провести</Button>
					<Button variant="danger" onClick={() => revert(Array.from(selectedRows))}>
						Распровести
					</Button>
				</div>
				<div
					ref={tableScrollRef}
					className={`tabel-table-scroll${showBusyCursor ? ' loading-more' : ''}`}
					onScroll={handleTableScroll}
				>
					{/* при догрузке следующей порции таблицу не гасим — строки остаются читаемыми */}
					<Table
						pending={pending && !loadingMore}
						headers={headers}
						rows={list}
						acceptRowClassName={acceptRowClassName}
					/>
				</div>
			</div>
			<RegenerateByUserModal
				isOpen={isRegenerateByUserOpen}
				onClose={() => setIsRegenerateByUserOpen(false)}
				onSuccess={refresh}
			/>
		</div>
	);
}
